import { ellipsisString, uuid } from "@/app/infrastructures/misc/Utils";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import jsPDF from "jspdf";
import { logoBlack, priorityPrint } from "../../constants/images";

export const createRoundRect = (
  doc: jsPDF | any,
  text: string,
  x: number,
  y: number
) => {
  doc.setFontSize( text === "INTERPACK MAXI" ? 6.5 : 7.5);
  doc.setFillColor("#333333");
  doc.setDrawColor("#333333");
  let res = 0;
  switch (text) {
    case "JUMBOPACK":
      res = text.length - 4.5;
      break;
    case "INTERPACK MAXI":
      res = text.length - 9;
      break;
    case "INTERPACK":
      res = text.length - 5;
      break;
    case "BOSSPACK":
      res = text.length - 3.5;
      break;
    case "JAGOPACK":
      res = text.length - 4;
      break;
    case "OTOPACK150":
    case "OTOPACK250":
      res = text.length - 5.5;
      break;
    case "BIGPACK FAST":
      res = text.length - 6.5;
      break;
    case "MINIPACK":
      res = text.length - 5.5;
      break;
    default:
      res = text.length - 3;
      break;
  }

  const length = res;
  doc.roundedRect(
    x,
    y,
    doc.getTextDimensions(text).w + length,
    doc.getTextDimensions(text).h + 1.8,
    1,
    1,
    "F"
  );

  doc.setTextColor("#FFFFFF");
  doc.setFont("Montserrat", "bold");
  doc.text(text, x + 1, y + 3.25 - (text === "INTERPACK MAXI" ? 0.25 : 0));
  return {
    w: doc.getTextDimensions(text).w + length,
    h: doc.getTextDimensions(text).h + 1.8
  };
};

const getPackAbbreviation = (fullName: string) => {
  const abbreviations: any = {
    BOSSPACK: "BOSSPACK",
    REGPACK: "REGPACK",
    JAGOPACK: "JAGOPACK",
    ONEPACK: "ONEPACK",
    INTERPACK: "INTERPACK",
    BIGPACK: "BIGPACK",
    JUMBOPACK: "JUMBOPACK",
    SAMEDAY: "SAMEDAY",
    OTOPACK150: "OTOPACK150",
    OTOPACK250: "OTOPACK250",
    INTERPACKMAXI: "INTERPACK MAXI",
    BIGPACKFAST: "BIGPACK FAST",
    MINIPACK: "MINIPACK"
  };

  return abbreviations[fullName] || fullName; // Return the abbreviation or the original fullName if not found
};

const renderLogo = (params: any, logoAspectRatio: number) => {
  params.doc.addImage(
    logoBlack,
    "PNG",
    params.startX + 5,
    params.startY + 108,
    16,
    logoAspectRatio * 16,
    "logo",
    "FAST",
    0
  );
};

const renderBarcode = (params: any) => {
  params.doc.setFont("Montserrat", "bold");
  params.doc.setFontSize(11);
  params.doc.setTextColor("#333333");
  params.doc.text(
    params.barcodeValue,
    params.startX + 5.5,
    params.startY + 116.5
  );
  params.doc.addImage(
    params.barcode,
    "PNG",
    params.startX + 6,
    params.startY + 117,
    41,
    8,
    uuid(),
    "FAST",
    0
  );
};

const renderCommodityText = (params: any, sttCommodityNamelanguage: string, isQuarantineKoordinat: number) => {
  params.doc.setFont("Montserrat", "normal");
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(4.5);
  
  const textConfig = params.data.isDangerousGoods 
    ? {
        text: `DG-${ellipsisString(sttCommodityNamelanguage, 25)}`,
        width: 15,
        xOffset: 13 + isQuarantineKoordinat
      }
    : {
        text: ellipsisString(sttCommodityNamelanguage, 30),
        width: 20,
        xOffset: params.data.sttCommodityIsQuarantine ? 10 : 5.5
      };

  params.doc.text(
    params.doc.splitTextToSize(textConfig.text, textConfig.width),
    params.startX + textConfig.xOffset,
    params.startY + 132
  );
};

const renderBookedName = (params: any) => {
  if (params.data.sttBookedName.toLowerCase() === "internal") return false;
  if (params.data.sttBookedName === params.data.sttBookedForName && params.data.sttClientId > 0) return false;

  let sttBookedNameLeftPosition = 5.2;
  if (params.data.isPriorityTier) {
    sttBookedNameLeftPosition = 11;
    params.doc.addImage(
      priorityPrint,
      "PNG",
      params.startX + 5.2,
      params.startY + 137,
      5,
      5,
      "logo-priority-print",
      "FAST"
    );
  }

  params.doc.setFont("Montserrat", "bold");
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(5);
  const splitedSttBooked = params.doc.splitTextToSize(
    `${ellipsisString(params.data.sttBookedName, 30)}`,
    19
  );
  const sttBookedY = splitedSttBooked.length > 1 ? 139 : 140;
  params.doc.text(
    splitedSttBooked,
    params.startX + sttBookedNameLeftPosition,
    params.startY + sttBookedY
  );
  return true;
};

export const RecipientHeaderSection = async (
  params: {
    doc: jsPDF;
    bookingType: string;
    accountType: string;
    data: PrintDataEntitas;
    pieceData: any;
    barcode: string;
    vendorRefCode: string;
    barcodeValue: string;
    vendorRefCodeValue: string;
    logo: string;
    shipmentId: string;
    startX: number;
    startY: number;
  },
  logoAspectRatio: number,
  language = "id"
) => {
  renderLogo(params, logoAspectRatio);
  const isInterpackMaxi = params.data.sttProductType === "INTERPACKMAXI";

  const isBigpackfast = params.data.sttProductType === "BIGPACKFAST";

  createRoundRect(
    params.doc,
    getPackAbbreviation(params.data.sttProductType),
    params.startX + (params.data.sttProductType === "JUMBOPACK" || params.data.sttProductType.includes("OTOPACK") ? 27 : 29 - (isInterpackMaxi ? 6 : isBigpackfast ? 5.75 : 0)),
    params.startY + 108
  );

  renderBarcode(params);

  const sttCommodityNamelanguage = language === "en" ? params.data.sttCommodityDescription : params.data.sttCommodityName;
  const isQuarantineKoordinat = params.data.sttCommodityIsQuarantine ? 4 : 0;
  const dgFontSize = 8;
  const renderBadge = (text: string, x: number) => {
    params.doc.setLineWidth(0.3);
    params.doc.setFillColor("#333333");
    params.doc.setDrawColor("#333333");
    params.doc.roundedRect(
      params.startX + x,
      params.startY + 130,
      params.doc.getTextDimensions(text, { fontSize: dgFontSize }).w + 1.5,
      params.doc.getTextDimensions(text, { fontSize: dgFontSize }).h + 2,
      0.5,
      0.5,
      "FD"
    );
    params.doc.setFont("Montserrat", "bold");
    params.doc.setTextColor("#FFFFFF");
    params.doc.setFontSize(dgFontSize);
    params.doc.text(text, params.startX + x + 0.8, params.startY + 133.3);
  };

  if (params.data.sttCommodityIsQuarantine) {
    renderBadge("K", 5.2);
  }

  if (params.data.isDangerousGoods) {
    renderBadge("DG", 5.2 + isQuarantineKoordinat);
  }

  renderCommodityText(params, sttCommodityNamelanguage, isQuarantineKoordinat);
  renderBookedName(params);
};
